import { useAuth } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Home, MessageCircle, User, LogOut, Settings, Search, MapPin, Vote, Sparkles, Heart, Bell, Info } from "lucide-react";
import { useNotifications } from "../contexts/NotificationContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const Layout = ({ children, hideBottomNav = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount, markAsRead } = useNotifications();

  // Handle Updates navigation with marking notifications as read
  const handleUpdatesClick = () => {
    markAsRead();
    navigate("/updates");
  };

  // First row of navigation
  const navItemsRow1 = [
    { path: "/search", icon: Search, label: "Search" },
    { path: "/feed", icon: Home, label: "Feed" },
    { path: "/polls", icon: Vote, label: "Polls" },
    { path: "/messages", icon: MessageCircle, label: "Messages" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  // Second row of navigation
  const navItemsRow2 = [
    { path: "/welcome", icon: Sparkles, label: "Welcome" },
    { path: "/near-me", icon: MapPin, label: "Near Me" },
    { path: "/donation", icon: Heart, label: "Donation" },
    { path: "/updates", icon: Bell, label: "Updates", hasNotification: true },
    { path: "/about", icon: Info, label: "About" },
  ];

  // Combined for desktop
  const allNavItems = [...navItemsRow1];

  const isActive = (path) => {
    if (path === "/messages") {
      return location.pathname.startsWith("/messages");
    }
    return location.pathname === path;
  };

  const getInitials = () => {
    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    if (user?.phone) {
      return user.phone.slice(-2);
    }
    return "??";
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
        <img 
          src={LOGO_URL} 
          alt="Lunawat Foundation" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
        />
      </div>
      
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto h-full px-4 flex items-center justify-between">
          {/* Logo */}
          <button 
            onClick={() => navigate("/feed")}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="logo-btn"
          >
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full object-cover" />
            <span className="text-xl font-bold hidden sm:inline">LUNAWAT FOUNDATION</span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {allNavItems.map((item) => (
              <Button
                key={item.path}
                variant={isActive(item.path) ? "secondary" : "ghost"}
                onClick={() => item.path && navigate(item.path)}
                className={`gap-2 ${isActive(item.path) ? 'font-semibold' : ''}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Button>
            ))}
          </nav>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-2 hover:opacity-80 transition-opacity outline-none"
                data-testid="user-menu-trigger"
              >
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.avatar} alt={user?.username} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium max-w-[100px] truncate">
                  {user?.username || user?.phone}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => navigate("/profile")}
                data-testid="menu-profile"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate("/profile/edit")}
                data-testid="menu-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-destructive focus:text-destructive"
                data-testid="menu-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className={`${hideBottomNav ? '' : 'pb-32 md:pb-0'}`}>
        {children}
      </main>

      {/* Mobile Bottom Navigation - Double Row */}
      {!hideBottomNav && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
          {/* Second Row (Top) */}
          <div className="flex items-center justify-around h-14 border-b border-border/50">
            {navItemsRow2.map((item, index) => (
              <button
                key={item.path || index}
                onClick={() => {
                  if (item.path === "/updates") {
                    handleUpdatesClick();
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                disabled={!item.path}
                className={`flex flex-col items-center justify-center gap-0.5 w-16 h-full transition-colors relative ${
                  item.path && isActive(item.path) 
                    ? 'text-primary' 
                    : item.path 
                      ? 'text-muted-foreground hover:text-foreground'
                      : 'text-muted-foreground/50'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <div className="relative">
                  <item.icon className={`w-5 h-5 ${item.path && isActive(item.path) ? 'stroke-[2.5]' : ''}`} />
                  {item.hasNotification && unreadCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-2 -right-2 h-4 min-w-4 px-1 text-[10px] flex items-center justify-center"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </div>
                <span className="text-[10px]">{item.label}</span>
              </button>
            ))}
          </div>
          
          {/* First Row (Bottom - Primary) */}
          <div className="flex items-center justify-around h-14">
            {navItemsRow1.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 w-16 h-full transition-colors ${
                  isActive(item.path) 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'stroke-[2.5]' : ''}`} />
                <span className="text-[10px]">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
};

export default Layout;
